import React from "react";

type PrimaryButtonProps = {
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;

  className?: string;
  textClassName?: string;

  id?: string;
  name?: string;
  ariaLabel?: string;
  "data-testid"?: string;
};

function cn(...classes: Array<string | undefined | false>) {
  return classes.filter(Boolean).join(" ");
}

export default function PrimaryButton({
  children,
  onClick,
  type = "button",
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  className,
  textClassName,
  id,
  name,
  ariaLabel,
  "data-testid": dataTestId,
}: PrimaryButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      id={id}
      name={name}
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={cn(
        "h-10",
        "rounded-[8px]",
        "bg-[#1C75BC] text-white",
        "text-[14px] font-semibold leading-[100%] tracking-[0em]",
        "shadow-[0_12px_25px_rgba(229,229,229,0.05)]",
        !isDisabled && "hover:brightness-110 active:brightness-95",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
        isDisabled && "opacity-60 cursor-not-allowed",
        className
      )}
    >
      <span className="flex h-full w-full items-center justify-center gap-2">
        {loading ? (
          <>
            <Spinner />
            <span className={textClassName}>Loading</span>
          </>
        ) : (
          <>
            {leftIcon && <span>{leftIcon}</span>}
            <span className={textClassName}>{children}</span>
            {rightIcon && <span>{rightIcon}</span>}
          </>
        )}
      </span>
    </button>
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
